import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Car } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { isSummerJobTypeValue } from '../../../../../utils/job/summer-job-job-type';
import type { DisplayJob } from '../../../../../types';
import { coerceStringArrayField } from '../../../../../utils/seo/coerce-string-array-field';
import { buildSrpFacetHref } from '../utils/buildSrpFacetHref';
import { Msg } from '../../../../../i18n-review/client/Msg';
import {
  JD_MORE_INFO_NATIVE_TITLE_TEXT,
  JD_MORE_INFO_NATIVE_TITLE_WRAP,
} from '../../../../../utils/job-detail/job-detail-more-info-affordance';

export interface JobDetailsSectionProps {
  displayJob: DisplayJob;
  optionalLanguages?: string[];
  perks?: string[];
  jobDetailsRef: React.RefObject<HTMLDivElement>;
}

export const JobDetailsSection: React.FC<JobDetailsSectionProps> = ({
  displayJob,
  optionalLanguages,
  perks,
  jobDetailsRef,
}) => {
  const intl = useIntl();

  // Calculate last seen freshness. Uses its own jobDetail.lastSeen.* message ids (distinct
  // from the hit.lastSeen.* ids the SRP hit cards use for the same concept) because those two
  // contexts want different phrasing ("Seen X ago" here vs. bare "X ago" on cards) — reusing one
  // id across differently-worded call sites would make whichever extracts last win for both.
  const lastSeenFreshness = displayJob?.lastSeen ? (() => {
    const now = new Date();
    const lastSeenDate = new Date(displayJob.lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    let freshnessId: 'jobDetail.lastSeen.minutesAgo' | 'jobDetail.lastSeen.hoursAgo' | 'jobDetail.lastSeen.daysAgo' =
      'jobDetail.lastSeen.daysAgo';
    let freshnessValue: number = diffDays;
    if (diffMins < 120) {
      freshnessId = 'jobDetail.lastSeen.minutesAgo';
      freshnessValue = diffMins;
    } else if (diffHours < 24) {
      freshnessId = 'jobDetail.lastSeen.hoursAgo';
      freshnessValue = diffHours;
    }
    return { freshnessId, freshnessValue };
  })() : null;

  // Check if content is complete for schema
  const isContentComplete = !!(
    displayJob?.title &&
    displayJob?.description &&
    displayJob?.companyId &&
    displayJob?.location &&
    (displayJob?.salaryRangeMin && displayJob?.salaryRangeMax) &&
    displayJob?.jobType &&
    displayJob?.normalisedRoleLevel
  );

  const missingFields = [];
  if (!displayJob?.title) missingFields.push('Title');
  if (!displayJob?.description) missingFields.push('Description');
  if (!displayJob?.companyId) missingFields.push('Company');
  if (!displayJob?.location) missingFields.push('Location');
  if (!displayJob?.salaryRangeMin || !displayJob?.salaryRangeMax) missingFields.push('Salary');
  if (!displayJob?.jobType) missingFields.push('Job Type');
  if (!displayJob?.normalisedRoleLevel) missingFields.push('Role Level');
  const missingTooltip = missingFields.length > 0 
    ? `Missing: ${missingFields.join(', ')}` 
    : 'Content Complete';

  // Coerce every listed field: records can carry these as a bare string or processor junk
  // ("[object Object]"), and a string passes a .length check then crashes .map during SSR.
  const benefits = coerceStringArrayField(displayJob?.benefits);
  const recruitmentProcess = coerceStringArrayField(displayJob?.recruitmentProcess);
  const skillsAndTechnologies = coerceStringArrayField(displayJob?.skillsAndTechnologies);
  const responsibilities = coerceStringArrayField(displayJob?.responsibilities);
  const requirements = coerceStringArrayField(displayJob?.requirements);
  const hasResponsibilities = responsibilities.length > 0;
  const hasRequirements = requirements.length > 0;
  const hasSkills = skillsAndTechnologies.length > 0;
  const hasEducation = !!displayJob.educationLevel;
  const hasPerks = !!(perks && perks.length > 0);
  const hasBenefits = benefits.length > 0;
  const facetLinkClass = 'underline decoration-jc-accent/40 underline-offset-2 hover:text-jc-accent';
  const facetHref = (facetKey: string, value: string) => buildSrpFacetHref(intl.locale, facetKey, value);

  return (
    <div ref={jobDetailsRef} data-section="jobDetails" className="scroll-mt-20 mt-10 pt-10 border-t border-jc-border" id="jobDetails">
      <h2 id="jobDetails-header" className="text-2xl font-bold text-jc-ink mb-6 tracking-tight">
        <Msg 
          id="jobDetailPage.section.jobDetails" 
          defaultMessage="Job Details" 
          description="Tab label for detailed job requirements section" 
        />
      </h2>

      <div id="jobDetails-benefits" className="space-y-5 scroll-mt-20">
        {hasResponsibilities && (
        <div className="rounded-xl border border-jc-border bg-jc-page/80 p-4">
          <h3 className="font-semibold text-lg text-jc-ink mb-3">
            <Msg id="jobDetailPage.responsibilities" defaultMessage="Responsibilities" description="Section heading for job responsibilities list" />
          </h3>
          <ul className="list-disc list-inside text-sm text-jc-ink-secondary space-y-1.5">
            {responsibilities.map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        )}

        {(hasRequirements || hasSkills) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {hasRequirements && (
          <div className="rounded-xl border border-jc-border bg-jc-surface/95 p-4">
            <h3 className="font-semibold text-lg text-jc-ink mb-3">
              <Msg id="jobDetailPage.requirements" defaultMessage="Requirements" description="Section heading for job requirements list" />
            </h3>
            <ul className="list-disc list-inside text-sm text-jc-ink-secondary space-y-1.5">
              {requirements.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          )}

          {hasSkills && (
          <div className="rounded-xl border border-jc-border bg-jc-surface/95 p-4">
            <h3 className="font-semibold text-lg text-jc-ink mb-3">
              <Msg id="jobDetailPage.skillsAndTechnologies" defaultMessage="Skills & Technologies" description="Section heading for skills and technologies list on job detail page" />
            </h3>
            <div className="flex flex-wrap gap-2">
              {skillsAndTechnologies.map((skill: string, index: number) => (
                <span key={`${skill}-${index}`} className="px-2.5 py-1 rounded-md bg-[#C8DDCE] dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8] text-xs font-medium border border-[#3B5E45]/20 dark:border-[#4A3520]">
                  {fixMojibake(skill)}
                </span>
              ))}
            </div>
          </div>
          )}
        </div>
        )}

        {hasEducation && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {hasEducation && (
          <div className="rounded-xl border border-jc-border bg-jc-surface/95 p-4">
            <h3 className="font-semibold text-base text-jc-ink mb-2.5">
              <Msg id="search.refinements.educationLevel.detail" defaultMessage="Education Level" description="Education level section heading on job detail page" />
            </h3>
            <a href={facetHref('educationLevel', displayJob.educationLevel!)} className={`text-sm text-jc-ink-secondary ${facetLinkClass}`}>
              {displayJob.educationLevel}
            </a>
          </div>
          )}
        </div>
        )}

        {(hasPerks || hasBenefits) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {hasPerks && (
          <div className="rounded-xl border border-jc-border bg-jc-page/80 p-4">
            <h3 className="font-semibold text-base text-jc-ink mb-2.5">
              <Msg id="jobDetailPage.perks" defaultMessage="Perks" description="Section heading for job perks and benefits list" />
            </h3>
            <ul className="list-disc list-inside text-sm text-jc-ink-secondary space-y-1.5">
              {perks!.map((perk: string, index: number) => (
                <li key={index}>
                  <a href={facetHref('perks', perk)} className={facetLinkClass}>
                    {fixMojibake(perk)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          )}
        </div>
        )}

        {(hasPerks || hasBenefits) && (
        <div className="rounded-xl border border-jc-border bg-jc-surface/95 p-4">
          <h3 className="font-semibold text-lg text-jc-ink mb-3">
            <Msg
              id="jobDetailPage.benefitsAndPerks"
              defaultMessage="Benefits & Perks"
              description="Section heading for combined benefits and perks on job detail page"
            />
          </h3>
          <div className="space-y-3">
            {hasBenefits && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-jc-muted mb-1">
                  <Msg id="jobDetailPage.benefits" defaultMessage="Benefits" description="Section heading for additional benefits list on job detail page" />
                </p>
                <div className="flex flex-wrap gap-2">
                  {benefits.map((benefit: string, index: number) => (
                    <a
                      key={`${benefit}-${index}`}
                      href={facetHref('benefits', benefit)}
                      className="px-2.5 py-1 rounded-md bg-[#F5EDD0] dark:bg-[#2A1E08] text-[#6E4F00] dark:text-[#D4A820] text-xs font-medium border border-[#B8860B]/25 dark:border-[#4A3520] hover:brightness-95 transition-[filter]"
                    >
                      {fixMojibake(benefit)}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {hasPerks && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-jc-muted mb-1">
                  <Msg id="jobDetailPage.perks" defaultMessage="Perks" description="Section heading for job perks and benefits list" />
                </p>
                <div className="flex flex-wrap gap-2">
                  {perks!.map((perk: string, index: number) => (
                    <a
                      key={`${perk}-${index}`}
                      href={facetHref('perks', perk)}
                      className="px-2.5 py-1 rounded-md bg-[#DDD5C8]/80 dark:bg-[#2A1D0F] text-[#5A3E20] dark:text-[#D4B898] text-xs font-medium border border-[#C4A882]/40 dark:border-[#4A3520] hover:brightness-95 transition-[filter]"
                    >
                      {fixMojibake(perk)}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {recruitmentProcess.length > 0 && (
        <div className="rounded-xl border border-jc-border bg-jc-surface/95 p-4">
          <h3 className="font-semibold text-lg text-jc-ink mb-3">
            <Msg id="jobDetailPage.recruitmentProcess" defaultMessage="Recruitment Process" description="Section heading for recruitment process timeline on job detail page" />
          </h3>
          <ol className="relative border-l border-jc-border ml-3">
            {recruitmentProcess.map((item: string, idx: number) => (
              <li key={idx} className="mb-6 ml-4 last:mb-0">
                <span className="absolute -left-3 flex items-center justify-center w-6 h-6 bg-jc-accent-muted rounded-full ring-2 ring-jc-surface">
                  <span className="text-jc-cta font-bold text-xs">{idx + 1}</span>
                </span>
                <div className="text-sm text-jc-ink-secondary font-medium pl-2 leading-relaxed">{fixMojibake(item)}</div>
                {idx < recruitmentProcess.length - 1 && <span className="block w-0.5 h-5 bg-jc-border absolute left-0 top-6"></span>}
              </li>
            ))}
          </ol>
        </div>
        )}
      </div>

      {/* Original Job Text Section - COMMENTED OUT FOR PERFORMANCE TESTING */}
      {/* {originalJobText && (
        <div className="mt-6 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
          {!showOriginalJobText ? (
            <button
              type="button"
              onClick={() => setShowOriginalJobText(true)}
              className="text-sm text-jc-accent hover:text-jc-cta-hover underline px-2 py-1 border border-jc-accent/25 rounded-lg hover:bg-jc-accent-muted/40 transition-colors cursor-pointer"
            >
              <Msg 
                id="jobDetailPage.showOriginalJobText" 
                defaultMessage="Show Original Job Text" 
                description="Button label to reveal the original job posting text before translation" 
              />
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  <Msg 
                    id="jobDetailPage.originalJobText" 
                    defaultMessage="Original Job Text" 
                    description="Section heading for the original untranslated job posting text" 
                  />
                </h3>
                <button
                  type="button"
                  onClick={() => setShowOriginalJobText(false)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline cursor-pointer"
                >
                  <Msg 
                    id="jobDetailPage.hideOriginalJobText" 
                    defaultMessage="Hide" 
                    description="Button label to hide the original job text section" 
                  />
                </button>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60">
                  {fixMojibake(originalJobText)}
                </div>
              </div>
            </div>
          )}
        </div>
      )} */}

      {/* Bottom row: Last Seen (left), Schema badge (right) */}
      <div className="flex flex-row justify-between items-end mt-4 pt-4 border-t border-jc-border">
        {/* Last Seen freshness indicator */}
        {lastSeenFreshness ? (
          <span
            className={`text-jc-accent text-xs font-semibold ${JD_MORE_INFO_NATIVE_TITLE_TEXT}`}
            title={displayJob.lastSeen}
          >
            {lastSeenFreshness.freshnessId === 'jobDetail.lastSeen.minutesAgo' ? (
              <Msg
                id="jobDetail.lastSeen.minutesAgo"
                defaultMessage="{value, plural, one {Seen {value} minute ago} other {Seen {value} minutes ago}}"
                values={{ value: lastSeenFreshness.freshnessValue }}
                description="Indicates how recently the job was last seen/crawled."
              />
            ) : lastSeenFreshness.freshnessId === 'jobDetail.lastSeen.hoursAgo' ? (
              <Msg
                id="jobDetail.lastSeen.hoursAgo"
                defaultMessage="{value, plural, one {Seen {value} hour ago} other {Seen {value} hours ago}}"
                values={{ value: lastSeenFreshness.freshnessValue }}
                description="Indicates how recently the job was last seen/crawled."
              />
            ) : (
              <Msg
                id="jobDetail.lastSeen.daysAgo"
                defaultMessage="{value, plural, one {Seen {value} day ago} other {Seen {value} days ago}}"
                values={{ value: lastSeenFreshness.freshnessValue }}
                description="Indicates how recently the job was last seen/crawled."
              />
            )}
          </span>
        ) : (
          <span />
        )}
        {/* Schema badge and job flags */}
        <span className="flex flex-row gap-2 items-center">
          {/* Job flag badges */}
          {isSummerJobTypeValue(displayJob?.jobType) && (
            <span
              className={`px-2 py-0.5 rounded-full bg-jc-gold-bg text-jc-gold text-xs font-semibold border border-jc-gold-line/25 ${JD_MORE_INFO_NATIVE_TITLE_WRAP}`}
              title="Summer Job"
            >
              <Msg 
                id="hit.summerJob" 
                defaultMessage="Summer Job" 
                description="Badge label indicating this is a summer/seasonal job position" 
              />
            </span>
          )}
          {displayJob?.isDriversLicenseRequired && (
            <div className={`relative group ${JD_MORE_INFO_NATIVE_TITLE_WRAP}`}>
              <Car className="h-4 w-4 text-jc-accent cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-jc-ink text-jc-surface text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                <Msg 
                  id="hit.driversLicenseRequired" 
                  defaultMessage="Driver's Licence Required" 
                  description="Badge label indicating this job requires a valid driver's license" 
                />
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-jc-ink"></div>
              </div>
            </div>
          )}
          {displayJob?.isOpenApplication && (
            <span
              className={`px-2 py-0.5 rounded-full bg-jc-accent-muted text-jc-cta text-xs font-semibold border border-jc-accent/25 dark:bg-[#2d4a38] dark:text-jc-accent dark:border-[#5A8A68]/40 ${JD_MORE_INFO_NATIVE_TITLE_WRAP}`}
              title="Open Application"
            >
              <Msg 
                id="hit.openApplication" 
                defaultMessage="Open Application" 
                description="Badge label indicating this is an open application position (accepting general applications, not for a specific role)" 
              />
            </span>
          )}
          {/* Schema badge */}
          {isContentComplete ? (
            <span className={JD_MORE_INFO_NATIVE_TITLE_WRAP} title="This job is content complete for Google and schema.org. Eligible for full rich results.">
              <img
                src="/site-media/data-analysis.png"
                alt="Content Complete"
                className="h-5 w-5 rounded-full shadow border border-jc-border"
                aria-label="Content Complete"
                loading="lazy"
              />
            </span>
          ) : (
            <span className={JD_MORE_INFO_NATIVE_TITLE_WRAP} title={missingTooltip}>
              <img
                src="/site-media/data-collection.png"
                alt="Partial Schema"
                className="h-5 w-5 rounded-full shadow border border-jc-border"
                aria-label="Partial Schema"
                loading="lazy"
              />
            </span>
          )}
        </span>
      </div>
    </div>
  );
};
